import { useEffect, useMemo, useRef, useState } from "react";
import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { density } from "@aeriosiq/ui";

import {
  useIiAssignableUsersQuery,
  useIiContractOptionsQuery,
  useIiContractPositionOptionsQuery,
} from "../../inspection-instructions/useInspectionInstructionFacets.js";
import type { AnchorChoice } from "../anchor.js";
import type { AnchorLevel, StandbyType } from "../types.js";
import { ENTITY_TYPE_BY_ANCHOR_LEVEL } from "../types.js";

/**
 * TT-04 — the back-office half of the create form: pick a pilot, then pick
 * where the standby lands, *within one project*.
 *
 * This exists because the pilot's own create form derives its anchor from
 * `GET /my-context` — the CALLER's assignments. A back-office user has none, so
 * that path renders "you have no open assignments" and dead-ends. Here the
 * anchor comes from the PROJECT being reviewed instead, walked down rather than
 * up: project → its parks (contracts) → that park's turbines (contract
 * positions), using the same `projects/v1` cascade the review page's scope
 * filter already uses. Same three levels, same vocabulary, opposite direction.
 *
 * The pilot list is `inspection-instructions/assignable-users` — the same set a
 * PM assigns a job to, so "who can I book time for" and "who can I send to the
 * turbine" cannot drift apart. XT-D9 name-withholding is honoured: a user whose
 * name the grant hides shows their tenant-scoped placeholder.
 */
export interface OnBehalfSelection {
  anchor: AnchorChoice | null;
  pilotUserId: string | null;
}

export interface OnBehalfAnchorFieldsProps {
  projectId: string;
  projectLabel: string | null;
  /** Drives the default level — weather books the park, technical the turbine. */
  activityType: StandbyType;
  /** Fires whenever the anchor or the pilot changes. */
  onChange: (selection: OnBehalfSelection) => void;
  disabled?: boolean;
}

const LEVEL_LABEL: Record<AnchorLevel, string> = {
  project: "Whole project",
  park: "One park",
  turbine: "One turbine",
};

export function OnBehalfAnchorFields({
  projectId,
  projectLabel,
  activityType,
  onChange,
  disabled = false,
}: OnBehalfAnchorFieldsProps) {
  // Weather covers a site, technical covers a machine — the same rule the
  // pilot's chips use, expressed as a starting level rather than a chip.
  const defaultLevel: AnchorLevel =
    activityType === "standby_weather" ? "park" : "turbine";

  const [level, setLevel] = useState<AnchorLevel>(defaultLevel);
  const [parkId, setParkId] = useState("");
  const [turbineId, setTurbineId] = useState("");
  const [pilotUserId, setPilotUserId] = useState("");
  // A hand-picked level must survive a type switch, exactly as a hand-picked
  // chip does on the pilot form.
  const [levelTouched, setLevelTouched] = useState(false);

  useEffect(() => {
    if (!levelTouched) setLevel(defaultLevel);
  }, [defaultLevel, levelTouched]);

  const parkOptions = useIiContractOptionsQuery(
    level === "park" || level === "turbine" ? projectId : null,
  );
  const turbineOptions = useIiContractPositionOptionsQuery(
    level === "turbine" && parkId ? parkId : null,
  );
  const pilotQuery = useIiAssignableUsersQuery(true);

  const pilots = useMemo(() => {
    const users = pilotQuery.data?.users ?? [];
    return users
      .map((u) => {
        const joined = [u.firstName, u.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
        // XT-D9: a withheld name falls back to its tenant-scoped placeholder,
        // never to a bare UUID. Empty strings are normalised to null FIRST so
        // the chain can use nullish coalescing — plain `??` on the raw values
        // would happily keep an empty string and render a nameless row.
        const name = joined.length > 0 ? joined : null;
        const placeholder =
          u.placeholder != null && u.placeholder.length > 0
            ? u.placeholder
            : null;
        const tenant =
          u.tenantName != null && u.tenantName.length > 0 ? u.tenantName : null;
        const label = name ?? placeholder ?? tenant ?? u.userId;
        return { id: u.userId, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [pilotQuery.data]);

  // Build the anchor from whatever is complete. An incomplete level yields
  // `null` so the dialog's validator refuses the save rather than silently
  // booking a level up.
  const anchor: AnchorChoice | null = useMemo(() => {
    if (level === "project") {
      return {
        level: "project",
        entityType: ENTITY_TYPE_BY_ANCHOR_LEVEL.project,
        entityId: projectId,
        label: projectLabel ? `Project · ${projectLabel}` : "Project",
      };
    }
    if (level === "park") {
      if (!parkId) return null;
      const name =
        parkOptions.data?.items.find((o) => o.id === parkId)?.label ?? null;
      return {
        level: "park",
        entityType: ENTITY_TYPE_BY_ANCHOR_LEVEL.park,
        entityId: parkId,
        label: name ? `Whole park · ${name}` : "Whole park",
      };
    }
    if (!turbineId) return null;
    const name =
      turbineOptions.data?.items.find((o) => o.id === turbineId)?.label ?? null;
    return {
      level: "turbine",
      entityType: ENTITY_TYPE_BY_ANCHOR_LEVEL.turbine,
      entityId: turbineId,
      label: name ?? "Turbine",
    };
    // The options queries ARE dependencies: their data landing is what
    // re-labels an id that was picked before the list arrived.
  }, [
    level,
    parkId,
    turbineId,
    projectId,
    projectLabel,
    parkOptions.data,
    turbineOptions.data,
  ]);

  // Report by VALUE, not by identity.
  //
  // `anchor` is rebuilt on every render the memo re-runs, and the memo depends
  // on the two query results — whose object identity changes on any refetch
  // even when the payload is byte-identical. Keying the effect on the object
  // therefore fires it on renders where nothing actually changed, and since
  // the parent responds with `setState`, that is an infinite loop. (It is not
  // theoretical: it reproduces the moment a test double returns a fresh object
  // per call, which is exactly what a refetch does.)
  //
  // So the effect compares a stable signature and only reports real changes.
  const lastReported = useRef<string | null>(null);
  useEffect(() => {
    const pilot = pilotUserId.length > 0 ? pilotUserId : null;
    const signature = JSON.stringify([
      anchor?.entityType ?? null,
      anchor?.entityId ?? null,
      anchor?.label ?? null,
      pilot,
    ]);
    if (lastReported.current === signature) return;
    lastReported.current = signature;
    onChange({ anchor, pilotUserId: pilot });
  }, [anchor, pilotUserId, onChange]);

  const selectWidth = { minWidth: density.comfortable.controlHeight * 5 };

  return (
    <Stack spacing={1.5}>
      <TextField
        select
        label="Pilot"
        value={pilotUserId}
        disabled={disabled}
        onChange={(e) => {
          setPilotUserId(e.target.value);
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-onbehalf-pilot" },
        }}
        helperText="Whose time this is. You are recorded as the person who booked it."
        fullWidth
      >
        <MenuItem value="">Pick a pilot…</MenuItem>
        {pilots.map((p) => (
          <MenuItem
            key={p.id}
            value={p.id}
            sx={{ minHeight: density.comfortable.controlHeight }}
          >
            {p.label}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          select
          label="Applies to"
          value={level}
          disabled={disabled}
          onChange={(e) => {
            setLevelTouched(true);
            setLevel(e.target.value as AnchorLevel);
            // A new level invalidates the ids picked at the old one.
            setParkId("");
            setTurbineId("");
          }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { "data-testid": "tt-onbehalf-level" },
          }}
          sx={selectWidth}
        >
          {(["project", "park", "turbine"] as const).map((l) => (
            <MenuItem key={l} value={l}>
              {LEVEL_LABEL[l]}
            </MenuItem>
          ))}
        </TextField>

        {level === "park" || level === "turbine" ? (
          <TextField
            select
            label="Park"
            value={parkId}
            disabled={disabled}
            onChange={(e) => {
              setParkId(e.target.value);
              setTurbineId("");
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "tt-onbehalf-park" },
            }}
            sx={selectWidth}
          >
            <MenuItem value="">Pick a park…</MenuItem>
            {(parkOptions.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}

        {level === "turbine" ? (
          <TextField
            select
            label="Turbine"
            value={turbineId}
            disabled={disabled || !parkId}
            onChange={(e) => {
              setTurbineId(e.target.value);
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "tt-onbehalf-turbine" },
            }}
            sx={selectWidth}
          >
            <MenuItem value="">Pick a turbine…</MenuItem>
            {(turbineOptions.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>

      {anchor ? (
        <Typography variant="caption" color="text.secondary">
          Booking against <strong>{anchor.label}</strong>.
        </Typography>
      ) : null}
    </Stack>
  );
}
